// Reads the question template (.xlsx) in the browser: every sheet as rows of cell text, by sheet name.
// An .xlsx file is a zip of XML files; only the workbook, its sheets and the shared strings are read.
import { strFromU8, unzipSync } from 'fflate';

const MAX_FILE = 5 * 1024 * 1024;        // the template with 500 questions is far smaller
const MAX_UNZIPPED = 40 * 1024 * 1024;
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

export class SheetError extends Error {}

function parse(text) {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new SheetError('This file looks damaged. Save it again in Excel and try once more.');
  return doc;
}

const MAX_COLS = 64;                     // the template uses 16; anything further right is ignored

function colIndex(ref, fallback) {
  const m = /^([A-Z]{1,3})\d{0,7}$/.exec(ref || '');
  if (!m) return fallback;
  let n = 0;
  for (const ch of m[1]) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

// Text of a shared or inline string, leaving out the phonetic guides some Excel versions add.
function stringText(el) {
  let out = '';
  for (const t of el.getElementsByTagName('t')) {
    if (t.parentNode && t.parentNode.localName === 'rPh') continue;
    out += t.textContent;
  }
  return out;
}

function readSheet(doc, shared) {
  const rows = [];
  let next = 1;
  for (const row of doc.getElementsByTagName('row')) {
    const r = Number(row.getAttribute('r')) || next;
    next = r + 1;
    const cells = [];
    let col = 0;
    for (const c of row.getElementsByTagName('c')) {
      const idx = colIndex(c.getAttribute('r'), col);
      col = idx + 1;
      if (idx >= MAX_COLS) continue;
      const type = c.getAttribute('t');
      const v = c.getElementsByTagName('v')[0];
      let value = '';
      if (type === 's') value = shared[Number(v && v.textContent)] || '';
      else if (type === 'inlineStr') value = stringText(c);
      else if (type === 'b') value = v && v.textContent === '1' ? 'TRUE' : 'FALSE';
      else value = v ? v.textContent : '';
      cells[idx] = value;
    }
    rows.push({ row: r, cells: Array.from(cells, (x) => (x == null ? '' : String(x))) });
  }
  return rows;
}

export function readWorkbook(buffer) {
  if (buffer.byteLength > MAX_FILE) throw new SheetError('That file is too big. Upload up to 500 questions at a time.');
  let total = 0;
  let files;
  try {
    files = unzipSync(new Uint8Array(buffer), {
      filter: (f) => {
        total += f.originalSize;
        if (total > MAX_UNZIPPED) throw new SheetError('That file is too big to read.');
        return /^xl\/(workbook\.xml|_rels\/workbook\.xml\.rels|sharedStrings\.xml|worksheets\/[^/]+\.xml)$/.test(f.name);
      },
    });
  } catch (e) {
    if (e instanceof SheetError) throw e;
    throw new SheetError('That isn’t an Excel file (.xlsx). Download the template, fill it in and upload it.');
  }
  if (!files['xl/workbook.xml']) throw new SheetError('That isn’t an Excel file (.xlsx). Download the template, fill it in and upload it.');
  const shared = [];
  if (files['xl/sharedStrings.xml']) {
    for (const si of parse(strFromU8(files['xl/sharedStrings.xml'])).getElementsByTagName('si')) shared.push(stringText(si));
  }
  const rels = {};
  if (files['xl/_rels/workbook.xml.rels']) {
    for (const r of parse(strFromU8(files['xl/_rels/workbook.xml.rels'])).getElementsByTagName('Relationship')) {
      rels[r.getAttribute('Id')] = r.getAttribute('Target');
    }
  }
  const sheets = {};
  const wb = parse(strFromU8(files['xl/workbook.xml']));
  for (const sh of wb.getElementsByTagName('sheet')) {
    const rid = sh.getAttributeNS(NS_REL, 'id') || sh.getAttribute('r:id');
    let target = rels[rid] || '';
    target = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
    if (files[target]) sheets[sh.getAttribute('name')] = readSheet(parse(strFromU8(files[target])), shared);
  }
  return sheets;
}

/* ---------- the question template ---------- */
const TYPES = {
  'multiple choice': 'mcq', mcq: 'mcq', 'multiple response': 'mr', mr: 'mr', number: 'number', 'number entry': 'number',
  'drop-down': 'dropdown', dropdown: 'dropdown', 'drop down': 'dropdown', written: 'written',
};
const LETTERS = 'ABCDEFGH';

const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

function columns(header) {
  const at = {};
  header.forEach((h, i) => { at[norm(h)] = i; });
  const find = (...names) => names.map((n) => at[n]).find((i) => i !== undefined);
  return {
    type: find('type'), topic: find('topic'), body: find('question'),
    options: LETTERS.split('').map((l) => find(`option ${l.toLowerCase()}`, `choice ${l.toLowerCase()}`)),
    correct: find('correct answer', 'answer'), statements: find('statements', 'drop-down statements'),
    marks: find('marks'), explanation: find('explanation or model answer', 'explanation', 'model answer'),
    difficulty: find('difficulty'), tolerance: find('tolerance', 'allowed difference'), case: find('case'),
  };
}

function letters(text) {
  const parts = String(text || '').toUpperCase().split(/[\s,;/&]+|\bAND\b/).filter(Boolean);
  return parts.map((p) => (/^[A-H]$/.test(p) ? LETTERS.indexOf(p) : NaN));
}

// Rows of the "Questions" sheet (and scenarios from "Cases") as questions for the database, with any
// problems the app can already see; the database checks everything again.
export function questionsFromWorkbook(sheets) {
  const name = Object.keys(sheets).find((n) => norm(n) === 'questions') || Object.keys(sheets).find((n) => !/^(how to|cases)/i.test(n));
  if (!name) throw new SheetError('No “Questions” sheet in this file. Download the template and use that.');
  const rows = sheets[name].filter((r) => r.cells.some((c) => String(c).trim()));
  const headerAt = rows.findIndex((r) => r.cells.some((c) => norm(c) === 'question') && r.cells.some((c) => norm(c) === 'type'));
  if (headerAt === -1) throw new SheetError('The “Questions” sheet needs its header row (Type, Topic, Question, …). Download the template and use that.');
  const col = columns(rows[headerAt].cells);
  const scenarios = {};
  const casesName = Object.keys(sheets).find((n) => norm(n) === 'cases');
  if (casesName) {
    const cr = sheets[casesName].filter((r) => r.cells.some((c) => String(c).trim()));
    const h = cr.findIndex((r) => r.cells.some((c) => norm(c) === 'case'));
    if (h !== -1) {
      const ci = cr[h].cells.findIndex((c) => norm(c) === 'case');
      const si = cr[h].cells.findIndex((c) => norm(c) === 'scenario');
      for (const r of cr.slice(h + 1)) if (r.cells[ci]) scenarios[norm(r.cells[ci])] = String(r.cells[si] || '').trim();
    }
  }
  const out = [];
  for (const r of rows.slice(headerAt + 1)) {
    const get = (i) => (i === undefined ? '' : String(r.cells[i] || '').trim());
    const typeText = norm(get(col.type));
    const type = TYPES[typeText];
    const options = col.options.map(get);
    const lastOption = options.reduce((last, o, i) => (o ? i : last), -1);
    const opts = options.slice(0, lastOption + 1);
    const q = { row: r.row, type: type || typeText, topic: get(col.topic), body: get(col.body), marks: get(col.marks),
      explanation: get(col.explanation), difficulty: get(col.difficulty).toLowerCase(), problems: [] };
    const caseName = get(col.case);
    if (caseName) { q.case_title = caseName; q.case_scenario = scenarios[norm(caseName)] || ''; }
    if (!type) q.problems.push('Type must be Multiple choice, Multiple response, Number, Drop-down or Written.');
    if (!q.body) q.problems.push('The question is empty.');
    const correct = get(col.correct);
    if (type === 'mcq' || type === 'mr') {
      q.options = opts;
      const picks = letters(correct);
      if (opts.some((o) => !o)) q.problems.push('Leave no gaps between options.');
      if (!picks.length || picks.some((n) => Number.isNaN(n) || n >= opts.length)) q.problems.push(`Correct answer: give the letter${type === 'mr' ? 's' : ''} of the right option${type === 'mr' ? 's (like A, C)' : ' (like B)'}.`);
      else if (type === 'mcq' && picks.length !== 1) q.problems.push('Multiple choice has one correct letter. Use Multiple response for more.');
      q.answer = type === 'mcq' ? picks[0] : [...new Set(picks)];
    } else if (type === 'number') {
      q.options = { prefix: '', suffix: '' };
      q.answer = { value: correct, tolerance: get(col.tolerance) };
      if (!correct) q.problems.push('Correct answer: give the number.');
    } else if (type === 'dropdown') {
      const statements = get(col.statements).split('\n').map((s) => s.trim()).filter(Boolean);
      q.options = { rows: statements, choices: opts };
      const picks = letters(correct);
      if (!statements.length) q.problems.push('Statements: one per line in the cell (Alt+Enter between them).');
      if (picks.length !== statements.length || picks.some((n) => Number.isNaN(n) || n >= opts.length)) {
        q.problems.push('Correct answer: one letter per statement, in order (like A, B, A).');
      }
      q.answer = picks;
    } else if (type === 'written') {
      q.options = [];
      q.answer = null;
    }
    if (q.marks && !/^\d+(\.5|\.0)?$/.test(q.marks)) q.problems.push('Marks must be a number, like 2 or 10.');
    if (q.difficulty && !['easy', 'medium', 'hard'].includes(q.difficulty)) q.problems.push('Difficulty can be Easy, Medium or Hard.');
    out.push(q);
  }
  if (!out.length) throw new SheetError('The “Questions” sheet has no questions under its header row.');
  if (out.length > 500) throw new SheetError(`This file has ${out.length} questions. Upload up to 500 at a time: split them over two files.`);
  return out;
}
